import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

gsap.registerPlugin(ScrollTrigger);

const { FiLinkedin, FiArrowUpRight } = FiIcons;

const recommendations = [
  {
    name: 'Brandon Young',
    title: 'VP Healthcare | International BPO Expert | Contact Center Services',
    relationship: 'Kathy\u2019s mentor',
    date: 'August 2023',
    isoDate: '2023-08-28',
    photo: '/assets/recommendations/brandon-young.jpg',
    linkedin: 'https://www.linkedin.com/in/brandon-young-48b2a818b/',
    quote:
      'I am honored to provide a sincere recommendation for Kathy, drawing from my extensive 18-year relationship with her. I had the pleasure of promoting and mentoring Kathy, giving her an opportunity in leadership. This day, everyone knew a rockstar was born!',
  },
  {
    name: 'Jonathan Smith',
    title: 'Call Center Operations Manager | Process Optimization',
    relationship: 'Reported to Kathy directly',
    date: 'September 2023',
    isoDate: '2023-09-18',
    photo: '/assets/recommendations/jonathan-smith.jpg',
    linkedin: 'https://www.linkedin.com/in/jonathan-smith-43b9bb70/',
    quote:
      'Kathy\u2019s commitment to people development is truly remarkable. She has an innate ability to identify potential in individuals and nurture it to fruition. Her coaching and mentoring skills are exceptional, and she has a unique ability to inspire and motivate her team.',
  },
  {
    name: 'Laura Coleman',
    title: 'Program Manager | Navigator | Ecosystem Builder',
    relationship: 'Reported to Kathy directly',
    date: 'March 2022',
    isoDate: '2022-03-26',
    photo: '/assets/recommendations/laura-coleman.jpg',
    linkedin: 'https://www.linkedin.com/in/lauraecoleman/',
    quote:
      'Kathy is an influential leader and mentor who challenges those around her to grow and elevate their skills. She is passionate about building trust within our team and has never wavered when it was time to take action and step in on our behalf. Her actions match her words and this is how trust is built.',
  },
  {
    name: 'Steve Eveland',
    title: 'Executive-Level Global Operations Professional',
    relationship: 'Managed Kathy directly',
    date: 'March 2019',
    isoDate: '2019-03-16',
    photo: null,
    linkedin: 'https://www.linkedin.com/in/steve-eveland-aa02322b/',
    quote:
      'Kathy is one of the most talented leaders I\u2019ve worked with in the BPO industry in my 30+ years. She brings innate character traits of integrity, reliability, teachability, positive attitude, and work ethic. She will definitely run a company someday.',
  },
];

const RecommendationsSection = () => {
  const containerRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(titleRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
          duration: 0.8,
          ease: 'power2.out',
        }
      );
      gsap.fromTo('.rec-card',
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top 75%',
            toggleActions: 'play none none none',
          },
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: 'Katherine William-Botrous',
            url: 'https://kwbotrous.com',
            review: recommendations.map((rec) => ({
              '@type': 'Review',
              author: { '@type': 'Person', name: rec.name, url: rec.linkedin },
              reviewBody: rec.quote,
              datePublished: rec.isoDate,
              itemReviewed: {
                '@type': 'Person',
                name: 'Katherine William-Botrous',
                jobTitle: 'Vice President of Sales',
              },
            })),
          })}
        </script>
      </Helmet>
      <div id="recommendations" ref={containerRef} className="relative bg-[#050505] text-white py-24 md:py-32 px-6 md:px-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(30,30,30,0.4),rgba(5,5,5,1))] pointer-events-none z-0"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent opacity-50 z-10"></div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <div ref={titleRef} className="mb-16">
          <div className="text-xs font-bold tracking-[0.28em] uppercase text-[#e2c96e] mb-4">
            What People Say
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter uppercase leading-none">
            Recommendations
          </h2>
          <p className="mt-4 text-gray-400 max-w-2xl">
            Endorsements from leaders and colleagues who have worked alongside Katherine across her career.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {recommendations.map((rec) => (
            <div
              key={rec.name}
              className="rec-card group bg-[#0d0d0d] border border-gray-800 p-8 flex flex-col justify-between transition-all duration-500 hover:-translate-y-1 hover:border-[#e2c96e] hover:shadow-[0_8px_30px_rgba(226,201,110,0.08)]"
            >
              <div>
                <div className="text-4xl text-[#e2c96e] font-serif leading-none mb-3 opacity-60 group-hover:opacity-100 transition-opacity duration-300">&ldquo;</div>
                <p className="text-gray-200 leading-relaxed mb-6">{rec.quote}</p>
              </div>
              <div className="flex items-center gap-4 border-t border-gray-800 pt-5">
                {rec.photo ? (
                  <img
                    src={rec.photo}
                    alt={`${rec.name} headshot`}
                    className="w-12 h-12 rounded-full object-cover border border-gray-700 transition-all duration-300 group-hover:border-[#e2c96e]"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#e2c96e] text-black flex items-center justify-center font-black text-sm transition-all duration-300 group-hover:ring-2 group-hover:ring-[#e2c96e]/40">
                    SE
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-bold text-sm">{rec.name}</div>
                  <div className="text-xs text-gray-400">{rec.title}</div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    {rec.relationship} &middot; {rec.date}
                  </div>
                </div>
                <a
                  href={rec.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${rec.name} on LinkedIn`}
                  className="text-gray-500 group-hover:text-[#e2c96e] transition-colors duration-300 group-hover:scale-110"
                >
                  <SafeIcon icon={FiLinkedin} className="text-xl" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </>
  );
};

export default RecommendationsSection;